import { useEffect, useState } from "react";
import { toast } from "sonner";
import { atlasFetch } from "@/lib/atlas-api";
import { Card, Skeleton, EmptyState, PrimaryButton, GhostButton } from "@/components/portal/PortalPrimitives";
import OneTimeRevealModal from "@/components/portal/OneTimeRevealModal";

const EVENT_OPTIONS = ["calculation.updated", "usage.threshold_reached"];

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

export default function PortalWebhooks() {
  const [cfg, setCfg] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ url: "", events: ["calculation.updated"], enabled: true });
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState(null);

  const [deliveries, setDeliveries] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const c = await atlasFetch("/api/v1/webhooks", { needsIngestSecret: true });
      setCfg(c);
      setForm({ url: c.url || "", events: c.events?.length ? c.events : ["calculation.updated"], enabled: c.enabled !== false });
    } catch (e) {
      toast.error(e.message);
      setCfg({ url: null, events: [], enabled: false, secret_masked: null, secret_set_at: null });
    }
  };

  const loadDeliveries = async (status = filter) => {
    try {
      const qs = new URLSearchParams({ limit: "100" });
      if (status && status !== "all") qs.set("status", status);
      const d = await atlasFetch(`/api/v1/webhooks/deliveries?${qs.toString()}`, { needsIngestSecret: true });
      setDeliveries(Array.isArray(d) ? d : d?.items || []);
    } catch (e) {
      setDeliveries([]);
    }
  };

  useEffect(() => { load(); loadDeliveries("all"); }, []);
  useEffect(() => { loadDeliveries(filter); }, [filter]);

  const toggleEvent = (ev) => {
    setForm((f) => ({
      ...f,
      events: f.events.includes(ev) ? f.events.filter((e) => e !== ev) : [...f.events, ev],
    }));
  };

  const save = async () => {
    if (!form.url) { toast.error("Webhook URL is required"); return; }
    setSaving(true);
    try {
      const r = await atlasFetch("/api/v1/webhooks", {
        method: "POST",
        body: JSON.stringify(form),
        needsIngestSecret: true,
      });
      if (r.secret) {
        setReveal({ title: "Webhook secret created", secret: r.secret });
      } else {
        toast.success("Webhook saved");
      }
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const rotateSecret = async () => {
    if (!window.confirm("Rotate the webhook signing secret? The current one stops working immediately.")) return;
    try {
      const r = await atlasFetch("/api/v1/webhooks/rotate-secret", { method: "POST", needsIngestSecret: true });
      setReveal({ title: "New webhook secret", secret: r.secret });
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const removeWebhook = async () => {
    if (!window.confirm("Delete the webhook configuration?")) return;
    try {
      await atlasFetch("/api/v1/webhooks", { method: "DELETE", needsIngestSecret: true });
      toast.success("Webhook removed");
      await load();
      setEditing(false);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Webhooks</p>
        <h1 className="font-display text-[28px] text-[#0A1128] mt-1.5">Configuration & deliveries</h1>
      </div>

      {/* Config */}
      {!cfg ? (
        <Card><Skeleton className="h-6 w-1/2" /><Skeleton className="mt-2 h-4 w-3/4" /></Card>
      ) : !editing ? (
        <Card testId="webhook-config-card">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[12px] uppercase tracking-[0.16em] text-slate-500">Endpoint</p>
              <p className="mt-1 font-mono text-[14px] text-[#0A1128] truncate">{cfg.url || <span className="text-slate-400">Not configured</span>}</p>
              <p className="mt-3 text-[12px] uppercase tracking-[0.16em] text-slate-500">Events</p>
              <p className="mt-1 text-[13px] text-slate-700">{(cfg.events || []).join(", ") || "—"}</p>
              <p className="mt-3 text-[12px] uppercase tracking-[0.16em] text-slate-500">Status</p>
              <p className="mt-1 text-[13px]">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${cfg.enabled ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-700 border border-slate-200"}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${cfg.enabled ? "bg-emerald-500" : "bg-slate-400"}`} />
                  {cfg.enabled ? "ENABLED" : "DISABLED"}
                </span>
              </p>
              {cfg.secret_masked && (
                <>
                  <p className="mt-3 text-[12px] uppercase tracking-[0.16em] text-slate-500">Signing secret</p>
                  <p className="mt-1 font-mono text-[12.5px] text-slate-700">{cfg.secret_masked}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Set {fmtDate(cfg.secret_set_at)}</p>
                </>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <PrimaryButton testId="webhook-edit-btn" onClick={() => setEditing(true)}>
                {cfg.url ? "Edit" : "Configure"}
              </PrimaryButton>
              {cfg.url && (
                <>
                  <GhostButton testId="webhook-rotate-btn" onClick={rotateSecret}>Rotate secret</GhostButton>
                  <button onClick={removeWebhook} className="text-[12px] text-rose-700 hover:text-rose-900">Delete webhook</button>
                </>
              )}
            </div>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="space-y-4">
            <div>
              <label className="text-[12px] font-medium text-slate-700">Endpoint URL</label>
              <input
                type="url"
                data-testid="webhook-url-input"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://your-app.example.com/webhooks/atlasforge"
                className="mt-1 w-full rounded-[10px] border border-slate-300 px-3 py-2 text-[14px] font-mono outline-none focus:border-[#1A4232]"
              />
            </div>
            <div>
              <label className="text-[12px] font-medium text-slate-700">Events</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {EVENT_OPTIONS.map((ev) => (
                  <button
                    type="button"
                    key={ev}
                    onClick={() => toggleEvent(ev)}
                    data-testid={`webhook-event-${ev}`}
                    className={`rounded-full px-3 py-1.5 text-[12.5px] border transition-colors ${
                      form.events.includes(ev) ? "bg-[#1A4232] text-[#F5F1EB] border-[#1A4232]" : "bg-white text-slate-700 border-slate-300"
                    }`}
                  >
                    {ev}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                data-testid="webhook-enabled-checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-[#1A4232] focus:ring-[#1A4232]"
              />
              Enabled
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <GhostButton onClick={() => setEditing(false)}>Cancel</GhostButton>
              <PrimaryButton testId="webhook-save-btn" onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </PrimaryButton>
            </div>
          </div>
        </Card>
      )}

      {/* Deliveries */}
      <div className="flex items-baseline justify-between flex-wrap gap-3 pt-4">
        <p className="font-display text-[18px] text-[#0A1128]">Recent deliveries</p>
        <div className="flex gap-1.5" data-testid="delivery-filter">
          {["all", "delivered", "failed"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`delivery-filter-${f}`}
              className={`rounded-full px-3 py-1 text-[12px] border transition-colors ${
                filter === f ? "bg-[#1A4232] text-[#F5F1EB] border-[#1A4232]" : "bg-white text-slate-700 border-slate-300"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {deliveries === null ? (
        <Card><Skeleton className="h-4 w-full" /><Skeleton className="mt-2 h-4 w-3/4" /></Card>
      ) : deliveries.length === 0 ? (
        <EmptyState testId="deliveries-empty" title="No deliveries yet" hint="Fire an event and we'll log every attempt here." />
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="deliveries-table">
              <thead className="bg-[#F5F1EB]/70 text-slate-700">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Event</th>
                  <th className="text-left px-4 py-3 font-semibold">End-user</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-4 py-3 font-semibold">Code</th>
                  <th className="text-right px-4 py-3 font-semibold">Tries</th>
                  <th className="text-right px-4 py-3 font-semibold">Duration</th>
                  <th className="text-left px-4 py-3 font-semibold">When</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d) => (
                  <tr key={d.id} className="border-t border-slate-100" data-testid={`delivery-${d.id}`}>
                    <td className="px-4 py-3 font-mono text-[12.5px] text-[#0A1128]">{d.event}</td>
                    <td className="px-4 py-3 font-mono text-[12px] text-slate-600">{d.end_user_id}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        d.status === "delivered" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"
                      }`}>
                        {d.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{d.response_code ?? "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{d.attempts}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">{d.duration_ms ? `${d.duration_ms}ms` : "—"}</td>
                    <td className="px-4 py-3 text-slate-500 text-[12px]">{fmtDate(d.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <OneTimeRevealModal
        open={!!reveal}
        title={reveal?.title || ""}
        secret={reveal?.secret || ""}
        onClose={() => setReveal(null)}
      />
    </div>
  );
}
